import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Building2, Landmark, Hotel, Trophy, Cpu, Users,
  ArrowRight, CheckCircle, Calendar, Truck, PartyPopper,
  Send, Loader2, BarChart3, MapPin, Clock, Shield
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageSEO from "@/components/shared/PageSEO";
import Breadcrumbs from "@/components/shared/Breadcrumbs";
import PartnerLogos from "@/components/shared/PartnerLogos";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const useCases = [
  { icon: Building2, title: "Corporate Conferences & Exhibitions", description: "Seamless mobility for corporate events, trade shows, and conferences across Saudi Arabia.", color: "from-sky-blue/20 to-sky-blue/5" },
  { icon: Landmark, title: "Government Events & National Celebrations", description: "Large-scale fleet deployment for national days, government summits, and public celebrations.", color: "from-eco-green/20 to-eco-green/5" },
  { icon: Hotel, title: "Hospitality & Hotel Campus Mobility", description: "Electric shuttles and scooters for hotel campuses, resorts, and hospitality venues.", color: "from-purple/20 to-purple/5" },
  { icon: Trophy, title: "Sports Events (Formula 1, Stadiums)", description: "Proven track record at Jeddah F1 and major sporting venues with rapid fleet deployment.", color: "from-amber-glow/20 to-amber-glow/5" },
  { icon: Cpu, title: "Smart City Pilot Programs", description: "Micro mobility pilots for NEOM, The Line, and smart city initiatives aligned with Vision 2030.", color: "from-teal-bright/20 to-teal-bright/5" },
  { icon: Users, title: "Hajj & Umrah Crowd Management", description: "Specialized patrol and transport vehicles for religious tourism and crowd flow management.", color: "from-rose/20 to-rose/5" },
];

const steps = [
  { icon: Calendar, title: "Tell Us Your Needs", description: "Share your event details, fleet size, and duration requirements." },
  { icon: Truck, title: "We Deliver Your Fleet", description: "We deploy, brand, and set up your vehicles on-site before your event." },
  { icon: PartyPopper, title: "You Enjoy, We Collect", description: "Focus on your event — we handle operations, support, and collection." },
];

const fleetVehicles = [
  { id: "itank", name: "iTank", tagline: "Electric Patrol Vehicle", image: "/products/itank/itank-hero.png" },
  { id: "itango", name: "iTango", tagline: "Three-Wheel EV", image: "/products/itango/itango-angle-1.png" },
  { id: "ilark", name: "iLark", tagline: "Electric Scooter", image: "/products/ilark/ilark-black-angle.png" },
  { id: "icart", name: "iCart ET300P2", tagline: "Electric Utility Cart", image: "/products/icart/et300p2-angle.png" },
];

const pricingTiers = [
  { label: "Daily", description: "Perfect for single-day events and exhibitions", badge: "Flexible" },
  { label: "Weekly", description: "Ideal for multi-day conferences and festivals", badge: "Popular" },
  { label: "Event Package", description: "Custom package for large-scale events with full support", badge: "Best Value" },
  { label: "Long-Term B2G", description: "Government contracts with dedicated fleet management", badge: "Enterprise" },
];

const stats = [
  { value: "500+", label: "Vehicles Deployed" },
  { value: "50+", label: "Events Supported" },
  { value: "30%", label: "Carbon Reduction at Events" },
  { value: "150+", label: "Vehicles at Jeddah F1" },
];

const faqs = [
  { q: "How do I rent electric vehicles for my event?", a: "Simply fill out the booking form on this page or contact us directly. Our team will assess your requirements including venue size, expected footfall, and duration to recommend the right fleet mix. We handle delivery, setup, operator training, and collection." },
  { q: "What is the minimum rental period?", a: "Our minimum rental period is one day. We offer daily, weekly, and event-based packages. For government and corporate clients, we also offer long-term contracts with dedicated fleet management and maintenance included." },
  { q: "Do you deliver across Saudi Arabia?", a: "Yes, LEMO operates across all major cities in Saudi Arabia including Jeddah, Riyadh, Dammam, Mecca, Medina, and NEOM. We handle all logistics including transportation, setup, and on-site technical support." },
  { q: "Can I customize branded vehicles for my event?", a: "Absolutely! We offer full vehicle branding and customization services. From custom wraps and decals to color matching your brand identity — our fleet customization team ensures your vehicles represent your brand perfectly at every event." },
];

const Lemo = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-contact-form", {
        body: { ...formData, subject: "LEMO Rental Inquiry" },
      });
      if (error) throw error;
      toast({ title: "Inquiry Sent!", description: "Our LEMO team will contact you within 24 hours." });
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast({ title: "Error", description: "Failed to send. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const lemoJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "LEMO - Event & Fleet Mobility Rentals",
      "provider": {
        "@type": "Organization",
        "name": "SDC - Sustainable Development Company",
        "url": "https://gulf-spark-rebuild.lovable.app"
      },
      "description": "Electric vehicle fleet rental service for events, corporate functions, and government operations in Saudi Arabia.",
      "areaServed": ["Jeddah", "Riyadh", "Dammam", "Mecca", "Medina", "NEOM"],
      "serviceType": "Electric Vehicle Fleet Rental",
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "SAR",
        "availability": "https://schema.org/InStock"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="LEMO - Electric Vehicle Rental Saudi Arabia"
        description="LEMO by SDC تطوير الاستدامة - Rent electric vehicle fleets for events, corporate functions & government operations in Saudi Arabia. تأجير سكوتر كهربائي للفعاليات. Daily, weekly & long-term B2B B2G fleet rental."
        keywords="electric scooter rental Saudi Arabia, تأجير سكوتر كهربائي, event mobility rental KSA, fleet rental Jeddah, electric vehicle hire B2B, تأجير مركبات كهربائية للفعاليات, تأجير أسطول كهربائي, LEMO rental, micro mobility rental, event fleet Saudi"
        jsonLd={lemoJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-eco-green/10 via-background to-sky-blue/10" />
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-eco-green/5 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-sky-blue/5 blur-3xl" />
        <div className="container mx-auto px-6 relative z-10">
          <Breadcrumbs />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mt-6">
            <Badge className="mb-4 bg-eco-green/20 text-eco-green border-eco-green/30 text-sm">
              🔋 LEMO by SDC
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4">
              Event & Fleet{" "}
              <span className="text-gradient-vibrant">Mobility Rentals</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-4 max-w-2xl leading-relaxed">
              Rent a fleet for your next event — delivered, managed, collected. Electric vehicles for corporate conferences, government events, sports stadiums, and smart city pilots across Saudi Arabia.
            </p>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl font-arabic">
              تأجير أسطول كهربائي للفعاليات والمؤتمرات والمشاريع الحكومية في المملكة العربية السعودية
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="xl" className="bg-gradient-to-r from-eco-green to-teal-bright text-white shadow-lg shadow-eco-green/25" asChild>
                <a href="#booking">
                  Request a Booking <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button variant="heroOutline" size="xl" asChild>
                <a href="#fleet">View Fleet</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">How It Works</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Three simple steps to get your electric fleet on-site</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
                <Card className="text-center h-full border-border/50 hover:border-eco-green/30 transition-colors">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-eco-green/10 flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-eco-green" />
                    </div>
                    <div className="text-sm font-semibold text-eco-green mb-2">Step {i + 1}</div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">B2B & B2G Use Cases</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Tailored mobility solutions for every sector</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((uc, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className="h-full hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${uc.color} flex items-center justify-center mb-4`}>
                      <uc.icon className="w-6 h-6 text-foreground" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">{uc.title}</h3>
                    <p className="text-sm text-muted-foreground">{uc.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Showcase */}
      <section id="fleet" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Available Rental Fleet</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Premium electric vehicles ready for your event</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {fleetVehicles.map((v, i) => (
              <motion.div key={v.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={`/products/${v.id}`}>
                  <Card className="overflow-hidden hover:border-eco-green/30 transition-all group">
                    <div className="aspect-[4/3] bg-gradient-to-b from-muted/30 to-muted/10 p-4 relative">
                      <img src={v.image} alt={v.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                      <Badge className="absolute top-3 right-3 bg-eco-green text-white border-0 text-xs">
                        Available for Rent
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">{v.name}</h3>
                      <p className="text-sm text-muted-foreground">{v.tagline}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Rental Plans</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Flexible pricing for every requirement</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {pricingTiers.map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className={`h-full text-center ${i === 2 ? "border-eco-green/50 shadow-lg shadow-eco-green/10" : "border-border/50"}`}>
                  <CardContent className="p-6">
                    <Badge variant={i === 2 ? "default" : "secondary"} className={i === 2 ? "bg-eco-green text-white mb-3" : "mb-3"}>
                      {tier.badge}
                    </Badge>
                    <h3 className="text-xl font-bold text-foreground mb-2">{tier.label}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
                    <Button variant="outline" size="sm" asChild>
                      <a href="#booking">Get Quote</a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats / Trust Signals */}
      <section className="py-16 bg-gradient-to-r from-eco-green/10 via-background to-sky-blue/10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-2">Saudi Event Mobility Statistics</h2>
            <p className="text-muted-foreground">Proven track record across Saudi Arabia's biggest events</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-eco-green mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Mobility Planning Guide */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mb-3 bg-sky-blue/20 text-sky-blue border-sky-blue/30">📋 Resource</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-3">Event Mobility Planning Guide</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">Essential tips for planning electric mobility at your Saudi event</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: MapPin, title: "Venue Assessment", desc: "Map your venue's pathways, entry points, and charging locations. Allow 1 vehicle per 500m² of patrol area." },
                { icon: Users, title: "Fleet Size Calculator", desc: "For events: 1 patrol vehicle per 2,000 attendees. For campus mobility: 1 shuttle per 200 guests." },
                { icon: Clock, title: "Timeline Planning", desc: "Book 2 weeks ahead for standard events. 4+ weeks for branded/customized fleet deployments." },
                { icon: Shield, title: "Safety & Compliance", desc: "All vehicles meet Saudi SASO standards. We provide operator training and safety briefings on-site." },
              ].map((tip, i) => (
                <Card key={i} className="border-border/50">
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-sky-blue/10 flex items-center justify-center shrink-0">
                      <tip.icon className="w-5 h-5 text-sky-blue" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{tip.title}</h3>
                      <p className="text-sm text-muted-foreground">{tip.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <PartnerLogos title="Trusted by Leading Organizations" />

      {/* FAQ */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-3">Frequently Asked Questions</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Everything you need to know about LEMO rentals</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-card border border-border rounded-2xl px-6">
                  <AccordionTrigger className="text-foreground font-semibold hover:no-underline">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground mb-3">Request a LEMO Booking</h2>
              <p className="text-muted-foreground">Tell us about your event and we'll prepare a custom fleet proposal</p>
            </div>
            <Card className="border-eco-green/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                      <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your name" required className="bg-background border-border" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" required className="bg-background border-border" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                      <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+966 XX XXX XXXX" className="bg-background border-border" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Company / Organization</label>
                      <Input name="company" value={formData.company} onChange={handleChange} placeholder="Company name" className="bg-background border-border" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Event Details *</label>
                    <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Describe your event, expected dates, venue, and fleet requirements..." rows={5} required className="bg-background border-border resize-none" />
                  </div>
                  <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-eco-green to-teal-bright text-white" disabled={isSubmitting}>
                    {isSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</> : <><Send className="w-5 h-5 mr-2" /> Submit Rental Inquiry</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Lemo;
